import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthContext } from '../contexts/AuthContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [customError, setCustomError] = useState(location.state?.customError || null);
  const { login, logout, sendPasswordReset, loading: authLoading, error } = useAuth();
  const { user, userProfile, loading: contextLoading } = useAuthContext();

  // パスワード再設定（リセット）用
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetSending, setResetSending] = useState(false);

  const handleSendReset = async () => {
    if (!resetEmail || resetSending) return;
    setResetSending(true);
    const res = await sendPasswordReset(resetEmail.trim());
    setResetSending(false);
    if (res.ok) setResetSent(true);
  };

  // ログイン済みの場合はリダイレクト、または未登録エラー
  useEffect(() => {
    if (contextLoading) return; // DBからの読み込み待ちはスキップ

    if (user) {
      if (!userProfile) {
        // DBにプロファイルが存在しない（Authのみ存在する未登録ユーザー）
        // セキュリティのため、アカウントの存在を特定できないよう汎用エラーメッセージにする
        setCustomError('メールアドレスまたはパスワードが正しくありません');
        logout();
        return;
      }

      const role = userProfile.role?.trim()?.toLowerCase();
      if (!role) {
        // ロールが設定されていない場合も同様
        setCustomError('メールアドレスまたはパスワードが正しくありません');
        logout();
        return;
      }
      
      if (role === 'owner') {
        const status = userProfile?.license?.status;
        if (status !== 'active') {
          setCustomError('アカウントが無効化されています。管理者にお問い合わせください。');
          logout();
          return;
        }
        // 期限切れはダッシュボードに入れて制限付きで使用可能
      }

      const redirect = location.state?.redirect;
      if (['superadmin', 'owner', 'staff'].includes(role)) {
        navigate(redirect || '/dashboard');
      } else if (role === 'guest') {
        navigate(redirect || '/portal');
      } else {
        setCustomError(`不明な権限です: ${role}`);
        logout();
      }
    }
  }, [user, userProfile, contextLoading, navigate, logout, location.state?.redirect]);

  const handleSubmit = async () => {
    if (!email || !password) return;
    setCustomError(null);
    const success = await login(email, password);
    if (success) {
      // useEffectでリダイレクト処理
    }
  };

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <img src="/logo-text.png" alt="LinkBoard" style={styles.logoImage} />
        </div>

        <div style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>メールアドレス</label>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@email.com"
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>パスワード</label>
            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="パスワードを入力"
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </div>

          {(error || customError) && <p style={styles.error}>{error || customError}</p>}

          <button
            style={{ ...styles.button, opacity: authLoading ? 0.7 : 1 }}
            onClick={handleSubmit}
            disabled={authLoading}
          >
            {authLoading ? 'ログイン中...' : 'ログイン'}
          </button>

          <button
            type="button"
            style={styles.forgotLink}
            onClick={() => { setShowReset(true); setResetEmail(email); setResetSent(false); }}
          >
            パスワードを忘れた方・招待リンクの期限が切れた方はこちら
          </button>
        </div>
      </div>

      {/* パスワード再設定モーダル */}
      {showReset && (
        <div style={styles.overlay} onClick={() => setShowReset(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            {resetSent ? (
              <>
                <h2 style={styles.modalTitle}>メールを送信しました</h2>
                <p style={styles.modalText}>
                  <b>{resetEmail}</b> 宛にパスワード設定用のメールを送信しました。<br />
                  メール内のリンクから新しいパスワードを設定してください。
                </p>
                <p style={styles.modalNote}>
                  ※ リンクの有効期限は約1時間です。届かない場合は迷惑メールフォルダもご確認ください。
                </p>
                <button style={styles.button} onClick={() => setShowReset(false)}>閉じる</button>
              </>
            ) : (
              <>
                <h2 style={styles.modalTitle}>パスワードの再設定</h2>
                <p style={styles.modalText}>
                  登録済みのメールアドレスを入力してください。<br />
                  パスワード設定用のリンクをお送りします。
                </p>
                <input
                  style={{ ...styles.input, width: '100%', padding: '14px 16px', fontSize: '15px', boxSizing: 'border-box' }}
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="example@email.com"
                  onKeyDown={(e) => e.key === 'Enter' && handleSendReset()}
                  autoFocus
                />
                {error && <p style={styles.error}>{error}</p>}
                <div style={styles.modalActions}>
                  <button style={styles.secondaryButton} onClick={() => setShowReset(false)}>キャンセル</button>
                  <button
                    style={{ ...styles.button, marginTop: 0, opacity: (!resetEmail || resetSending) ? 0.6 : 1 }}
                    onClick={handleSendReset}
                    disabled={!resetEmail || resetSending}
                  >
                    {resetSending ? '送信中...' : '送信する'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f4f6fb'
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '470px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
  },
  logo: {
    textAlign: 'center',
    marginBottom: '32px'
  },
  logoImage: {
    width: '300px',
    height: 'auto',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#555'
  },
  input: {
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    fontSize: '14px',
    outline: 'none'
  },
  button: {
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    background: '#6c63ff',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px'
  },
  error: {
    color: '#ef4444',
    fontSize: '13px',
    margin: 0
  },
  forgotLink: {
    background: 'none',
    border: 'none',
    color: '#6c63ff',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    textAlign: 'center',
    padding: '4px',
    marginTop: '4px',
    textDecoration: 'underline',
  },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)', padding: '20px',
  },
  modal: {
    background: '#fff', padding: '32px', borderRadius: '16px', width: '100%', maxWidth: '520px',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
  },
  modalTitle: { fontSize: '19px', fontWeight: '700', color: '#1e293b', margin: '0 0 12px 0' },
  modalText: { fontSize: '14px', color: '#475569', lineHeight: 1.7, margin: '0 0 16px' },
  modalNote: { fontSize: '12px', color: '#94a3b8', lineHeight: 1.6, margin: '0 0 20px' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' },
  secondaryButton: {
    background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px 20px',
    borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
  },
};

export default LoginPage;
